import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { first } from 'rxjs';
import { SettingsService } from '@shared/api/settings.service';
import { AuthenticationService } from '@shared/authentication/authentication.service';
import { BaseObject } from '@shared/base/base-object';
import { AppScrollStrategy } from '@shared/config/scroll-strategy';
import { CityLiving, OrderState } from '@shared/states/order.state';
import { UserState } from '@shared/states/user.state';
import { ThemeState } from '@shared/states/theme.state';
import { getDialogPosition } from '@ui/dialog/dialog-position';
import { DialogService } from '@ui/dialog/dialog.service';
import { ChangeLocationFactory } from '@ui/navigation-panel/dialogs/change-location/change-location.factory';
import { ProfileMenuComponent } from '../dialogs/profile-menu/profile-menu.component';
import { AppState, DeferActionEnum } from '@shared/states/app-state.service';
import {
  SendStatisticsService,
  StatisticsActionKey,
  StatisticsContextEnum,
} from '@api/send-statistics.service';
import { ChangeLocationResult } from '../dialogs/change-location/change-location.types';
import { NavPanelState } from '@shared/states/nav-panel.state';

@Component({
  selector: 'app-account-nav',
  templateUrl: './account-nav.component.html',
  styleUrls: ['./account-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountNavComponent extends BaseObject {
  @Input() public colorState: 'light' | 'dark';
  @Input() public isMarketingFlow: boolean;

  @ViewChild('accountIcon', { read: ElementRef }) public set content(content: ElementRef) {
    if (content) {
      this.accountIcon = content;
    }
  }

  public get isWizardPage(): boolean {
    return location.pathname === '/wizard/order-create-flow';
  }

  public get isFlatPage(): boolean {
    return location.pathname === '/configurator/flat';
  }

  public get isLoginPage(): boolean {
    return location.pathname === '/login';
  }

  private accountIcon: ElementRef;

  constructor(
    private appState: AppState,
    public navPanelState: NavPanelState,
    public orderState: OrderState,
    public themeState: ThemeState,
    public userState: UserState,
    private authService: AuthenticationService,
    public settingsService: SettingsService,
    private dialogService: DialogService,
    private statistics: SendStatisticsService,
    private router: Router,
  ) {
    super();
  }

  public openProfileMenu() {
    this.dialogService.open<ProfileMenuComponent, void>(ProfileMenuComponent, null, {
      width: '280px',
      height: 'auto',
      position: getDialogPosition(this.accountIcon.nativeElement, 'right'),
      autoFocus: false,
      backdropClass: 'app-dialog-no-backdrop',
      scrollStrategy: new AppScrollStrategy(),
    });
  }

  public changeLocation(): void {
    const mobile = this.settingsService.screenBreakpoint$.value === 'XXSmall';

    this.statistics
      .sendStat(StatisticsActionKey.CHANGE_LOCATION_BUTTON, StatisticsContextEnum.STEP_WIZARD_1)
      .pipe(first())
      .subscribe();

    this.dialogService
      .openLazy<void, ChangeLocationResult>({ factory: ChangeLocationFactory }, null, {
        width: mobile ? 'calc(100% - 32px)' : '530px',
        mobile: mobile,
      })
      .closed$.pipe(first())
      .subscribe((result) => {
        if (!result) {
          this.statistics
            .sendStat(
              StatisticsActionKey.CHANGE_LOCATION_CLOSE_BUTTON,
              StatisticsContextEnum.CHANGE_LOCATION,
            )
            .pipe(first())
            .subscribe();

          return;
        }

        if (result.city) {
          this.orderState.currentCity$.next(result.city);
        } else if (result.manualSelect) {
          this.navPanelState.manualSelect$.next(true);
          this.navPanelState.manualSelect$.next(false);
        }
      });
  }

  public toLogin(): void {
    this.appState.deferActions$.next([{ type: DeferActionEnum.Routing, url: location.pathname }]);
    this.statistics
      .sendStat(StatisticsActionKey.MENU_AUTH, StatisticsContextEnum.MAIN_BOARD_CONTEXT_MENU)
      .pipe(first())
      .subscribe();

    this.router.navigate(['login']);
  }
}
